// Copyright © 2017-2019  SDG Systems, LLC. All rights reserved.
// This file may not be used in any form without the express written permission of SDG Systems, LLC

<template>
  <v-container fluid grid-list-md>
    <v-card>
      <v-toolbar flat>
        <v-btn icon @click="exit"><v-icon>clear</v-icon></v-btn>
        <v-toolbar-title v-if="newUser">Add User</v-toolbar-title>
        <v-toolbar-title v-else>Edit User</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn v-if="canModify && !newUser" dark color="green" @click="showResetDialog = true">Reset Password</v-btn>
        <v-btn v-if="canModify" dark color="blue" @click="saveUser">Save</v-btn>
        <v-btn v-if="canModify && !newUser" dark color="red" @click="showDeleteDialog = true">Delete</v-btn>
      </v-toolbar>
      <v-card-text>
        <v-form v-model="formValid" ref="form">
          <v-layout row wrap>
            <v-flex xs12 md6>
              <v-text-field
                name="input"
                label="Name"
                v-model="user.name"
                :rules="[ruleRequired]"
                :disabled="!canModify"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 md6>
              <v-text-field
                name="input"
                label="Username"
                :rules="[ruleEmail]"
                v-model="user.username"
                :disabled="!canModify"
              ></v-text-field>
            </v-flex>
            <v-flex xs12>
              <v-autocomplete
                v-bind:items="roles"
                v-model="user.roles"
                item-text="name"
                item-value="id"
                multiple
                chips
                label="User Roles"
                :rules="[ruleOnePlus]"
                :disabled="!canModify"
              ></v-autocomplete>
            </v-flex>
            <v-flex xs12 md6 v-if="newUser">
              <v-checkbox
              v-model="sendEmail"
              label="Send User Activation Email"
              ></v-checkbox>
            </v-flex>
            <v-flex v-if="newUser && !sendEmail" xs12 md6>
              <v-text-field
                name="input"
                label="Password"
                v-model="user.password"
                required
                :rules="[rulePassword]"
                type="password"
                counter :min="passwordLength">
              ></v-text-field>
            </v-flex>
          </v-layout>
        </v-form>
      </v-card-text>
    </v-card>

    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>
          <div class="headline">Confirm Delete</div>
        </v-card-title>
        <v-card-text>Are you sure you want to delete this user?  <b>This action cannot be undone.</b></v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click.native="showDeleteDialog = false">Cancel</v-btn>
          <v-btn flat color="red" @click="deleteUser()">I'm Sure</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <password-reset v-model="showResetDialog" :user="user" :endpoint="endpoint"></password-reset>
    <error-snackbar v-model="errorText"></error-snackbar>
  </v-container>
</template>

<script>
  import { mapGetters } from 'vuex'
  import ApiRequests from '@/api/ApiRequests'
  import {PERMISSIONS} from 'vue-common/components/constants'
  import {ENDPOINT} from '@/shared/constants'
  import {checkPermission} from 'vue-common/components/user'
  import {AXIOSAPI} from 'vue-common/components/requests-common'
  import {getUserRolesText, getUserRoles} from 'vue-common/components/utils'
  import PasswordReset from 'vue-common/components/PasswordReset'
  import ErrorSnackbar from 'vue-common/components/ErrorSnackbar'
  import Rules from 'vue-common/components/rules'

  export default {
    components: {
      ErrorSnackbar,
      PasswordReset
    },

    data () {
      return {
        endpoint: ENDPOINT,
        errorText: '',
        formValid: null,
        passwordLength: Rules.PASSWORDLENGTH,
        passwordReset1: '',
        passwordReset2: '',
        roles: [],
        showDeleteDialog: false,
        showResetDialog: false,
        sendEmail: true,
        // User needs these empty fields or the field rules don't like it.
        user: {
          password: '',
          username: '',
          securityCtx: {}
        }
      }
    },

    created () {
      this.getUser()
      this.getRoles()
    },

    computed: {
      ...mapGetters({
        activeAccessRules: 'accessRules',
        activeUser: 'user',
        isSuperAdmin: 'isSuperAdmin'
      }),

      canModify: function () {
        return checkPermission(this.activeAccessRules, 'users', PERMISSIONS.MODIFY)
      },

      newUser: function () {
        return (this.$route.params.id === 'new')
      },

      passwordsEqual: function () {
        if (this.passwordReset1 === this.passwordReset2) {
          return true
        } else {
          return 'Passwords are not the same'
        }
      },

      userRolesText: function () {
        return getUserRolesText(this.user.roles, this.roles)
      },

      userRoles: function () {
        var x = getUserRoles(this.user.roles, this.roles)
        return x
      }
    },

    methods: {
      deleteUser: function () {
        ApiRequests.deleteUser(this.user.id).then(r => {
          this.exit()
        }).catch(e => {
          this.errorText = e.response.statusText + ': ' + e.response.data
        })
      },

      getUser: function () {
        if (this.newUser) {
        } else {
          ApiRequests.getUser(this.$route.params.id).then(r => {
            this.user = r.data
          }).catch(e => {
            this.errorText = e.response.statusText + ': ' + e.response.data
          })
        }
      },

      getRoles: function () {
        ApiRequests.getRoles().then((response) => {
          this.roles = response.data
          if (!this.isSuperAdmin) {
            var index = this.roles.findIndex((el) => el.name === 'Super Admin')
            if (index > -1) {
              this.roles.splice(index, 1)
            }
          }
        })
      },

      saveUser: function () {
        if (this.$refs.form.validate()) {
          if (this.newUser) {
            ApiRequests.postUser(this.user, this.sendEmail).then(response => {
              this.exit()
            }).catch(e => {
              console.log(e)
              this.errorText = e.response.statusText + ': ' + e.response.data
            })
          } else {
            ApiRequests.putUser(this.user, this.sendEmail).then(response => {
              if (this.user.id === this.activeUser.id) {
                AXIOSAPI.post('auth/logout')
              }
              this.exit()
            }).catch(e => {
              console.log(e)
              this.errorText = e.response.statusText + ': ' + e.response.data
            })
          }
        } else {
          this.errorText = 'Please fill out all required fields'
        }
      },

      ruleEmail: function (address) {
        return Rules.email(address)
      },

      ruleOnePlus: function (x) {
        if (this.ruleRequired(x)) {
          if (x.length > 0) {
            return true
          } else {
            return 'You must select at least one item'
          }
        }
        return this.ruleRequired(x)
      },

      ruleRequired: function (x) {
        return Rules.required(x)
      },

      rulePassword: function (password) {
        return Rules.password(password)
      },

      exit: function () {
        this.$router.push({name: 'users'})
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/UserEdit.vue